import React from 'react';
import { motion } from 'framer-motion';
import { FiActivity, FiTrendingUp, FiBookOpen, FiChevronRight, FiCpu, FiAward, FiBell } from 'react-icons/fi';

const FeatureGrid = () => {
    const features = [
        {
            icon: <FiActivity />,
            title: 'Physical Class',
            desc: 'Direct instruction at premium institutes with real-time interaction and hands-on O/L Maths problem solving.',
            color: 'bg-blue-600'
        },
        {
            icon: <FiTrendingUp />,
            title: 'Online Class',
            desc: 'Interactive virtual learning for O/L Maths with crystal-clear audio-visuals and instant feedback.',
            color: 'bg-white/10'
        },
        {
            icon: <FiBookOpen />,
            title: 'Archived Payloads',
            desc: 'Flexible learning with ultra-HD recorded sessions you can access anytime to bridge knowledge gaps.',
            color: 'bg-blue-600'
        },
        {
            icon: <FiCpu />,
            title: 'Exam Blueprints',
            desc: 'Comprehensive study packs featuring model papers, syllabus breakdowns, and systematic revision guides.',
            color: 'bg-white/10'
        }
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: "easeOut" }
        }
    };

    return (
        <section className="py-24 px-6 max-w-7xl mx-auto bg-[#0a0a0a]">
            <div className="text-center mb-24">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="flex items-center justify-center gap-4 mb-4"
                >
                    <div className="h-[2px] w-8 bg-blue-500"></div>
                    <span className="text-blue-500 font-bold uppercase tracking-widest text-xs">Our Ecosystem</span>
                    <div className="h-[2px] w-8 bg-blue-500"></div>
                </motion.div>
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    viewport={{ once: true }}
                    className="text-3xl md:text-5xl font-black tracking-tight text-white uppercase"
                >
                    LEARNING <span className="text-blue-500">MODALITIES</span>
                </motion.h2>
            </div>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-8"
            >
                {features.map((item, idx) => (
                    <motion.div
                        key={idx}
                        variants={cardVariants}
                        whileHover={{ y: -10, scale: 1.02 }}
                        className="bg-white/[0.03] backdrop-blur-xl rounded-[1.5rem] md:rounded-[2.5rem] p-6 md:p-12 flex flex-col items-center text-center border border-white/5 hover:border-blue-500/40 hover:bg-white/[0.05] transition-all duration-500 group relative overflow-hidden"
                    >
                        {/* Glowing Background Effect */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-transparent to-blue-500/0 group-hover:from-blue-500/5 group-hover:to-transparent transition-all duration-700 pointer-events-none"></div>

                        {/* Icon Container */}
                        <div className={`w-12 h-12 md:w-20 md:h-20 rounded-2xl md:rounded-3xl mb-4 md:mb-8 flex items-center justify-center text-xl md:text-3xl transition-all duration-500 group-hover:scale-110 shadow-2xl ${item.color} text-white`}>
                            {item.icon}
                        </div>

                        <h3 className="text-sm md:text-xl font-black text-white mb-3 md:mb-6 tracking-tight group-hover:text-blue-400 transition-colors relative z-10">
                            {item.title}
                        </h3>

                        <p className="text-[10px] md:text-sm text-white/40 font-medium leading-relaxed group-hover:text-white/60 transition-colors relative z-10 line-clamp-3 md:line-clamp-none">
                            {item.desc}
                        </p>

                        {/* Decorative HUD Corner */}
                        <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-white/0 group-hover:border-blue-500/20 transition-all duration-500 rounded-tr-[1.5rem] md:rounded-tr-[2.5rem]"></div>
                    </motion.div>
                ))}
            </motion.div>
        </section>
    );
};

export default FeatureGrid;
